.<style lang="scss" scoped>
.table-row {
    color: #212529;
    min-width: 100%;
    padding: 0 16px;
    display: flex;
    flex-flow: row;
    width: max-content;
    &.can-hover {
        cursor: pointer;
        transition: background 0.2s;
        &:hover {
            background: var(--white-hover);
        }
    }
    &:not(:last-child) {
        position: relative;
        &:after {
            content: "";
            width: calc(100% - 32px);
            position: absolute;
            bottom: 0;
            border-bottom: 1px solid var(--border-color);
        }
    }
    .table-cell {
        display: flex;
        padding-top: 16px;
        padding-bottom: 16px;

        .avatar {
            margin: auto 16px auto 0;
            flex: none;
        }
        .extra {
            box-shadow: -8px 0 12px rgba(0, 0, 0, 0.06);
            border-radius: 3px;
            background-color: #ffffff;
        }
        .label {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            width: 100%;
        }
    }
    .today {
        height: 30px;
        border-radius: 2px;
        background-color: #0f3261;
        color: #fff;
        line-height: 30px;
        padding: 0 13px;
    }
    .green {
        color: #40db4c;
    }
    .orange {
        color: #ff9312;
    }
    .red {
        color: #f73453;
    }
}
</style>
<template>
    <a :href="url" class="table-row flex-shrink-0" @click="maybeClickTrough">
        <div class="table-cell" v-for="(value, index) in structure.filter((s) => s.visible )" :key="index" :class="['type-' + value.type, 'size-' + value.size]" @click="handleClick(value.type)">

            <p-check v-if="value.type === 'select'" v-model="data.selected" class="p-curve p-svg p-jelly my-auto mr-0" color="primary" name="check">
                <i-check slot="extra" class="svg svg-icon"></i-check>
            </p-check>

            <template v-if="value.type === 'user'">
                <div class="avatar sm text-avatar d-flex align-items-center justify-content-center">
                    <span v-if="data.first_name && data.first_name">{{ data.first_name.charAt(0) }} {{ data.last_name.charAt(0) }}</span>
                    <i-trash v-else class="i-8 fill-primary-dark"></i-trash>
                </div>
                <div class="small my-auto">
                    {{ data.first_name || $t('table.codes.deleted') }}
                    {{ data.last_name || null }}
                </div>
            </template>

            <template v-if="value.type === 'text' || value.type === 'number'">
                <div class="small my-auto">
                    <template v-if="value.key.split('.').length === 1">{{ (data[value.key] ? value.prefix || '' : '') + (data[value.key] || '') }}</template>
                    <template v-if="value.key.split('.').length === 2">{{ (value.prefix || '') + (data[value.key.split('.')[0]][value.key.split('.')[1]]||'') }}</template>
                </div>
            </template>

            <template v-if="value.type === 'translated'">
                <div class="small my-auto">
                    {{ $t(`table.${data[value.key]}`) }}
                </div>
            </template>

            <template v-if="value.type === 'date'">
                <div class="small my-auto">
                    {{ data[value.key] | moment('DD MMM YYYY') }}
                </div>
            </template>

            <template v-if="value.type === 'follow_up_date'">
                <div class="small my-auto" :class="[...followUpColor(data[value.key]), ...value.class]">
                    <template v-if="!data[value.key]">{{ $t('pages.patients.not_followed_up_yet') }}</template>
                    <template v-else-if="$moment().isSame(data[value.key], 'day')">
                        {{ $t('pages.patients.today') }}
                    </template>
                    <template v-else-if="$moment().subtract(1, 'days').isSame(data[value.key], 'day')">
                        {{ $t('pages.patients.yesterday') }}
                    </template>
                    <template v-else>
                        {{ data[value.key] |  moment("from", "now") }}
                    </template>
                </div>
            </template>

            <template v-if="value.type === 'paid'">
                <span class="small text-success my-auto" v-if="data[value.key] !== null">€{{ parseFloat(data[value.key]).toFixed(2) }}</span>
                <i-cross v-else slot="extra" class="i-20 my-auto"></i-cross>
            </template>
            <template v-if="value.type === 'followup'">
                <i-checkmark v-if="data[value.key].includes('rate.initial') || data[value.key].includes('rate.upgrade')" slot="extra" class="i-20 my-auto"></i-checkmark>
                <i-cross v-else slot="extra" class="i-20 my-auto"></i-cross>
            </template>

            <template v-if="value.type === 'boolean'">
                <i-checkmark v-if="data[value.key]" slot="extra" class="i-20 my-auto"></i-checkmark>
                <i-cross v-else slot="extra" class="i-20 my-auto"></i-cross>
            </template>

            <template v-if="value.type === 'label'">
                <div class="small my-auto bg-primary-dark text-light px-8 py-4 rounded">
                    {{ data[value.key] }}
                </div>
            </template>
            <template v-if="value.type === 'labels'">
                <div class="d-flex flex-row flex-wrap">
                    <div v-for="(label, index) in data[value.key].slice(0,labels)" :key="index" style="flex: 1 0 calc(50% - 4px);" :class="{'mt-8': index > 1, 'my-auto': index <= 1}" class="small bg-primary-dark text-light mr-4 px-8 py-4 rounded label" v-html="data[value.key][index].startsWith('profession_type.') || data[value.key][index].startsWith('psychological_complaint.') || data[value.key][index].startsWith('rate.') ? $t(`table.labels.${data[value.key][index]}`) :data[value.key][index]" :title="data[value.key][index].startsWith('profession_type.') || data[value.key][index].startsWith('psychological_complaint.') || data[value.key][index].startsWith('rate.') ? $t(`table.labels.${data[value.key][index]}`) :data[value.key][index]" @click="labels = 1">
                    </div>
                    <div v-if="data[value.key].length - 1 > 0 && labels === 1" class="small my-auto extra p-4" @click="labels = data[value.key].length">
                        +{{ data[value.key].length - 1 }}
                    </div>
                </div>
            </template>

        </div>
        <div v-if="editable" class="table-cell table-edit px-0 text-right ml-auto size-edit">
        </div>

    </a>
</template>

<script>
import { PCheck } from '@/components/forms'

export default {
    components: { PCheck },
    props: {
        data: {
            type: Object,
            required: true
        },
        structure: {
            type: Array,
            required: true
        },
        editable: {
            type: Boolean,
            default: true
        },
        url: {
            type: String,
            default: '#'
        }
    },
    data() {
        return {
            labels: 1
        }
    },
    methods: {
        maybeClickTrough(event) {
            if (event.target.type !== 'checkbox')
                event.preventDefault()

        },
        handleClick(type) {
            if (type !== 'select' && type !== 'labels')
                this.$emit('click')
        },
        followUpColor(date) {
            if (!date) return ['default']

            let ago = this.$moment().diff(date, 'days')

            if (ago <= 6) return ['green']
            if (ago <= 13) return ['orange']
            if (ago > 13) return ['red']
        }
    }
}
</script>
